import { AddCircleOutline, HighlightOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import songjukti from "../../images/songjukti_logo.png";
import PriceSummaryRow from "./PriceSummaryRow";
import { SERVICE_FEE_RATE, formatCurrencyAmount, getCurrencyLabel, splitByServiceFee } from "./priceUtils";
import { tokens } from "../../store/tokens";

const inputSx = {
  borderRadius: `${tokens.input.radius}px`,
  bgcolor: tokens.input.bg,
  fontSize: tokens.input.fontSize.sm,
  "& fieldset": { borderColor: tokens.input.border },
  "&:hover fieldset": { borderColor: tokens.input.borderHover },
  "&.Mui-focused fieldset": { borderColor: tokens.input.borderFocus },
};

const emptyMilestone = (id, currency) => ({
  id,
  ms_description: "",
  ms_date: "",
  ms_amount: "",
  ms_currency: currency,
});

const MilestoneMultiForm = ({ setCpInfo, cpInfo, fetchJobDetails }) => {
  const currency = fetchJobDetails?.currency;
  const [milestoneFields, setMilestoneFields] = useState([emptyMilestone(0, currency)]);
  const [nextId, setNextId] = useState(1);

  const addMilestoneFields = () => {
    setMilestoneFields((prev) => [...prev, emptyMilestone(nextId, currency)]);
    setNextId((prev) => prev + 1);
  };

  const handleMilestoneFormChange = (index, event) => {
    const { name, value } = event.target;
    setMilestoneFields((prev) =>
      prev.map((field, i) =>
        i === index ? { ...field, [name]: value, ms_currency: currency } : field
      )
    );
  };

  const handleDeleteMS = (id) => {
    setMilestoneFields((prev) => prev.filter((field) => field.id !== id));
  };

  // Total = sum of every milestone amount (what the client is quoted).
  const totalAmount = useMemo(
    () =>
      milestoneFields.reduce(
        (sum, field) => sum + (parseFloat(field.ms_amount) || 0),
        0
      ),
    [milestoneFields]
  );

  // charge = 15% of the total; receiveAmount = total - charge.
  const { charge, receiveAmount } = useMemo(
    () => splitByServiceFee(totalAmount),
    [totalAmount]
  );

  // Single effect keeps cpInfo in sync with the milestone list and the
  // derived totals — no scattered setCpInfo calls inside every handler,
  // so there's no risk of pushing a stale milestones array up to the parent.
  useEffect(() => {
    setCpInfo((prev) => ({
      ...prev,
      milestones: milestoneFields,
      budget: totalAmount,
      budgetCharge: charge,
      clientPrice: receiveAmount,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestoneFields, totalAmount, charge, receiveAmount]);

  return (
    <Stack spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
      <Typography
        sx={{
          fontFamily: tokens.font.body,
          fontSize: "1.05rem",
          fontWeight: 700,
          color: tokens.color.ink,
        }}
      >
        How many milestones do you want to include?
      </Typography>

      <Stack spacing={2} sx={{ width: "100%" }}>
        {milestoneFields.map((input, index) => (
          <Box
            key={input.id}
            sx={{
              width: "100%",
              border: `1px solid ${tokens.color.line}`,
              borderRadius: `${tokens.radius.md}px`,
              padding: "16px",
              bgcolor: tokens.bg.surfaceMuted,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  fontFamily: tokens.font.body,
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: tokens.color.ink,
                }}
              >
                Milestone {index + 1}
              </Typography>
              {milestoneFields.length > 1 && (
                <IconButton
                  aria-label={`remove milestone ${index + 1}`}
                  size="small"
                  onClick={() => handleDeleteMS(input.id)}
                  sx={{
                    color: tokens.color.inkFaint,
                    "&:hover": { color: tokens.color.error },
                  }}
                >
                  <HighlightOff fontSize="small" />
                </IconButton>
              )}
            </Stack>

            <TextareaAutosize
              aria-label={`milestone ${index + 1} description`}
              minRows={2}
              placeholder="Describe what's delivered in this milestone"
              name="ms_description"
              value={input.ms_description}
              style={{
                fontFamily: tokens.font.body,
                fontSize: "0.875rem",
                width: "100%",
                boxSizing: "border-box",
                border: `1px solid ${tokens.input.border}`,
                borderRadius: `${tokens.input.radius}px`,
                padding: "10px 14px",
                color: tokens.color.ink,
                resize: "vertical",
              }}
              onChange={(event) => handleMilestoneFormChange(index, event)}
            />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 1.5 }}
            >
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: tokens.font.body,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: tokens.color.inkMuted,
                  }}
                >
                  Due date
                </Typography>
                <OutlinedInput
                  name="ms_date"
                  type="date"
                  size="small"
                  value={input.ms_date}
                  fullWidth
                  sx={inputSx}
                  onChange={(event) => handleMilestoneFormChange(index, event)}
                />
              </Stack>

              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: tokens.font.body,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: tokens.color.inkMuted,
                  }}
                >
                  Amount
                </Typography>
                <OutlinedInput
                  name="ms_amount"
                  type="number"
                  size="small"
                  value={input.ms_amount}
                  fullWidth
                  placeholder="Enter amount"
                  startAdornment={
                    <InputAdornment position="start">
                      <Typography
                        sx={{
                          fontFamily: tokens.font.body,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: tokens.color.inkMuted,
                        }}
                      >
                        {getCurrencyLabel(currency)}
                      </Typography>
                    </InputAdornment>
                  }
                  sx={inputSx}
                  onChange={(event) => handleMilestoneFormChange(index, event)}
                />
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>

      <Button
        onClick={addMilestoneFields}
        startIcon={<AddCircleOutline fontSize="small" />}
        size="small"
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          fontWeight: 600,
          color: tokens.color.teal,
          borderRadius: `${tokens.radius.pill}px`,
          border: `1px solid ${tokens.color.teal}`,
          px: 2,
          "&:hover": { bgcolor: tokens.color.tealLight },
        }}
      >
        Add milestone
      </Button>

      <Divider sx={{ width: "100%", borderColor: tokens.color.line }} />

      <Box
        sx={{
          width: "100%",
          borderRadius: `${tokens.radius.md}px`,
          border: `1px solid ${tokens.color.line}`,
          overflow: "hidden",
          bgcolor: tokens.bg.surface,
        }}
      >
        <PriceSummaryRow
          label="Total price of project"
          description="This includes all milestones, and is the amount your client will see"
          value={formatCurrencyAmount(totalAmount, currency)}
        />
        <PriceSummaryRow
          icon={
            <Box
              component="img"
              src={songjukti}
              alt=""
              sx={{ width: 16, height: 16, borderRadius: "50%" }}
            />
          }
          label={`Charge (${Math.round(SERVICE_FEE_RATE * 100)}%)`}
          value={formatCurrencyAmount(charge, currency)}
        />
        <PriceSummaryRow
          label="You'll Receive"
          description="Your estimated payment, after service fees"
          value={formatCurrencyAmount(receiveAmount, currency)}
          highlight
          isLast
        />
      </Box>
    </Stack>
  );
};

export default MilestoneMultiForm;